import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { User, UserRole } from '../../data/mockUsers';
import { useAppStore } from '../../contexts/AppStore';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
  mode: 'add' | 'edit';
}

export const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  user,
  mode,
}) => {
  const { addUser, updateUser, roles } = useAppStore();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'consulta' as UserRole,
    department: '',
    phone: '',
    status: 'active' as 'active' | 'inactive',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Cargar datos del usuario si estamos en modo edición
  useEffect(() => {
    if (mode === 'edit' && user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        department: user.department || '',
        phone: user.phone || '',
        status: user.status,
      });
    } else {
      // Resetear formulario en modo agregar
      setFormData({
        name: '',
        email: '',
        role: 'consulta',
        department: '',
        phone: '',
        status: 'active',
      });
    }
    setErrors({});
  }, [mode, user, isOpen]);
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!formData.role) {
      newErrors.role = 'El rol es requerido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (mode === 'add') {
      const newUser: User = {
        id: Date.now().toString(),
        name: formData.name,
        email: formData.email,
        role: formData.role,
        department: formData.department || undefined,
        phone: formData.phone || undefined,
        status: formData.status,
        createdAt: new Date().toISOString(),
        lastLogin: undefined,
      };
      addUser(newUser);
    } else if (mode === 'edit' && user) {
      updateUser(user.id, {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        department: formData.department || undefined,
        phone: formData.phone || undefined,
        status: formData.status,
      });
    }
    
    onClose();
  };
  
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Limpiar error del campo cuando el usuario empieza a escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Agregar Nuevo Usuario' : 'Editar Usuario'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información Básica */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Información Básica</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nombre Completo *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
              placeholder="Ej: Juan Pérez"
            />
            
            <Input
              label="Email *"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              placeholder="usuario@universidad.edu"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Teléfono"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+34 912 345 678"
            />
            
            <Input
              label="Departamento"
              name="department"
              value={formData.department}
              onChange={handleChange}
              placeholder="Ej: OTRI, Ingeniería"
            />
          </div>
        </div>
        
        {/* Rol y Permisos */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Rol y Permisos</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol *
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.role ? 'border-red-500' : ''
              }`}
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role}</p>
            )}
            
            {/* Mostrar descripción del rol seleccionado */}
            {formData.role && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-900 font-medium">
                  {roles.find(r => r.id === formData.role)?.name}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {roles.find(r => r.id === formData.role)?.description}
                </p>
                <div className="mt-2">
                  <p className="text-xs font-medium text-blue-900 mb-1">Permisos:</p>
                  <ul className="text-xs text-blue-700 space-y-0.5">
                    {roles.find(r => r.id === formData.role)?.permissions.map((permission, idx) => (
                      <li key={idx}>• {permission}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Estado */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Estado</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado del Usuario
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Los usuarios inactivos no podrán acceder al sistema
            </p>
          </div>
        </div>
        
        {/* Botones de acción */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
          >
            {mode === 'add' ? 'Agregar Usuario' : 'Guardar Cambios'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
